import classNames from 'classnames';
import React from 'react';

import OutlineClose from '@/assets/images/outline-close.svg';
import UploadImage from '@/assets/images/tray-arrow-up.svg';
import type { AssetDocument, OcrDetail, StipDocument } from './types';

interface DocumentFormRowProps {
  documents: Array<StipDocument | AssetDocument>;
  data: Record<string, { filePreview?: string }>;
  isAsset?: boolean;
  filesOcrDetails?: OcrDetail[];
  duplicateErrors?: Record<string, string>;
  handleFile: (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string,
    stipRequestItemId?: string,
    skipOcrCheck?: boolean
  ) => void;
  removeFile: (fileName: string, type: string) => void;
}

const isStipDocument = (doc: StipDocument | AssetDocument): doc is StipDocument =>
  'stipRequestItemId' in doc;

const DocumentFormRow = ({
  documents,
  data,
  isAsset = false,
  filesOcrDetails = [],
  duplicateErrors = {},
  handleFile,
  removeFile,
}: DocumentFormRowProps): React.ReactNode => {
  return (
    <>
      {documents.map((document, index) => {
        const isStip = isStipDocument(document);
        const stipRequestItemId = isStip ? document.stipRequestItemId : undefined;

        const fileKey = stipRequestItemId
          ? `document_file${stipRequestItemId}`
          : `asset_document_${index}`;

        const hasLocalUpload = Boolean(data[fileKey]);

        const ocrMatch =
          isStip && document.stipTypeName
            ? filesOcrDetails.find(
                (f) =>
                  f.stipTypeName?.trim().toLowerCase() ===
                  document.stipTypeName?.trim().toLowerCase()
              )
            : undefined;

        const showOcrError = ocrMatch?.isValid === false && ocrMatch.message;

        const getStatusMessage = (): string => {
          if (hasLocalUpload) return '';
          if (isStip && duplicateErrors[document.stipRequestItemId])
            return duplicateErrors[document.stipRequestItemId];
          const isReceivedDatePresent =  !!document.stipReceivedDate || !!document.phoenixUserUploadedDate;
          if (!isReceivedDatePresent) return 'Pending Submission';
          if (document.validationMessages?.[0]) return document.validationMessages[0];
          return 'Received';
        };

        const statusMessage = getStatusMessage();
        const isReceived =
                  !!(document.stipReceivedDate || document.phoenixUserUploadedDate) &&
                  !document.validationMessages?.[0] &&
                  !showOcrError &&
                  !duplicateErrors[document.stipRequestItemId];
        const showUploadIcon = !isReceived;

        return (
          <div
            key={
              isStip
                ? `${document.stipRequestItemId}-${document.stipTypeId}`
                : document.entityId
            }
            className="document-form"
          >
            <div className="document-form__info">
              <div className="document-form__doc-type">
                {document.stipTypeName ?? ''}
              </div>

              {'stipTypeDescription' in document && (
                <div className="document-form__description">
                  {document.stipReceivedDate || document.phoenixUserUploadedDate ? '' : document.stipTypeDescription}
                </div>
              )}

              <br />

              <div
                className={classNames('document-form__status', {
                  'document-form__status--received': statusMessage === 'Received',
                  'document-form__status--none': statusMessage !== 'Received'
                })}
              >
                {statusMessage}
              </div>
            </div>

            {showUploadIcon && (
              <div className="document-form__input-container">
                {hasLocalUpload && (
                  <>
                    <img
                      className="document-form__uploaded-image"
                      src={data[fileKey].filePreview}
                    />
                    <div className="document-form__remove-image-container">
                      <img
                        className="document-form__remove-image"
                        onClick={() =>
                          removeFile(
                            fileKey,
                            isAsset ? 'assetDocumentsData' : 'data'
                          )
                        }
                        src={OutlineClose}
                      />
                    </div>
                  </>
                )}

                {!hasLocalUpload && showUploadIcon && isStip && (
                  <>
                    <input
                      id={fileKey}
                      className="document-form__input"
                      name={fileKey}
                      type="file"
                      accept=".pdf,.doc,.docx,.png,.gif,.jpg,.jpeg,.txt"
                      onChange={(e) =>
                        handleFile(
                          e,
                          isAsset ? 'assetDocumentsData' : 'data',
                          document.stipRequestItemId,
                          Boolean(
                            document.stipReceivedDate ||
                              document.phoenixUserUploadedDate ||
                              document.validationMessages?.[0] ||
                              showOcrError
                          )
                        )
                      }
                    />
                    <label htmlFor={fileKey} className="document-form__image-container">
                      <img
                        className="document-form__input-upload"
                        src={UploadImage}
                      />
                    </label>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export default DocumentFormRow;
